import React from "react";
import { Modal, List, Tag, Button, Space, Empty, Divider } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  UserOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { dayStatuses } from "../../core/utils/weekPlanUtils";
import styles from "./WorkStatusCalendar.module.css";

// Status meta for display
const statusMetaMap = {
  0: { label: "Pending", color: "default" },
  1: { label: "Requested", color: "blue" },
  2: { label: "Approved", color: "green" },
  3: { label: "Rejected", color: "red" },
  4: { label: "Cancelled", color: "orange" },
};

const ApplicationDetailsModal = ({
  visible,
  onClose,
  date,
  applications = [],
  isAdmin = false,
  onApprove,
  onReject
}) => {
  const { t } = useTranslation();

  const formatDate = (dateStr) => {
    return dayjs(dateStr).format("DD.MM.YYYY");
  };

  const getStatusMeta = (statusCode) => {
    return statusMetaMap[statusCode] || statusMetaMap[0];
  };

  const getDayStatusName = (reasonCode) => {
    const status = dayStatuses[reasonCode];
    if (status) {
      return t(`reasons.${status.toLowerCase()}`) || status;
    }
    return t("unknown");
  };

  const handleApprove = (appKey) => {
    if (onApprove) {
      onApprove(appKey);
    }
  };

  const handleReject = (appKey) => {
    if (onReject) {
      onReject(appKey);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          {t("applicationsFor") || "Applications for"}{" "}
          {date ? date.format("DD.MM.YYYY") : ""}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t("close")}
        </Button>
      ]}
      width={600}
      className={styles.detailsModal}
    >
      {applications.length === 0 ? (
        <Empty description={t("noApplications") || "No applications for this date"} />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={applications}
          renderItem={(app) => {
            const statusMeta = getStatusMeta(app.statusCode);
            const isRequested = app.statusCode === 1; // Only "Requested" can be approved/rejected

            return (
              <List.Item
                className={styles.applicationItem}
                actions={
                  isAdmin && isRequested
                    ? [
                        <Button
                          key="approve"
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={() => handleApprove(app.key)}
                          className={styles.approveButton}
                        >
                          {t("approve")}
                        </Button>,
                        <Button
                          key="reject"
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={() => handleReject(app.key)}
                        >
                          {t("reject")}
                        </Button>
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  avatar={
                    <div className={styles.avatarContainer}>
                      <UserOutlined className={styles.userIcon} />
                    </div>
                  }
                  title={
                    <Space>
                      <span className={styles.employeeName}>
                        {app.userName || t("unknownUser")}
                      </span>
                      <Tag color={statusMeta.color}>
                        {t(statusMeta.label)}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div className={styles.applicationDetails}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t("reason")}:</span>
                        <Tag>{getDayStatusName(app.reasonCode)}</Tag>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t("period")}:</span>
                        <span>
                          {formatDate(app.from)} - {formatDate(app.to)}
                        </span>
                      </div>
                      {(app.reasonCode === 1 || app.reasonCode === 5) && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t("holidayDaysColumn")}:</span>
                          <span>{app.holidayDays != null ? app.holidayDays : "—"}</span>
                        </div>
                      )}
                      {app.comment && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t("commentColumn")}:</span>
                          <span>{app.comment}</span>
                        </div>
                      )}
                      {app.email && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t("email")}:</span>
                          <span className={styles.email}>{app.email}</span>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Modal>
  );
};

export default ApplicationDetailsModal;
